import { Button } from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { IconText } from '@delmonicos/shared-design-system-react/src/components/icon-text/icon-text';
import {
  Icon,
  IconColor,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { getDuration } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/get-duration';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { TransactionStateName } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/enums/transaction/transaction-state.enum';
import { TransactionsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction/paginated/transactions-paginated.model';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetTransactionsPaginatedQuery } from '@app/core/service/cpms-bo-transaction.service';

import { ChargePointChannelTransactionStartController } from '../charge-point-channel-transaction-start/charge-point-channel-transaction-start.controller';
import { ChargePointChannelTransactionStopController } from '../charge-point-channel-transaction-stop/charge-point-channel-transaction-stop.controller';
import {
  ChargePointChannelTransactionContextProvider,
  useChargePointChannelContext,
} from '../charge-point.context';
import * as css from './charge-point-channel-transaction-details.module.scss';

interface ChargePointChannelTransactionDetailsControllerProps {
  resource: TransactionsPaginatedModel;
}

export const ChargePointChannelTransactionDetailsController = ({
  resource,
}: ChargePointChannelTransactionDetailsControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const navigate = useNavigate();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  // Nothing to do

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionStartIsDialogOpen, setActionStartIsDialogOpen] = useState(false);
  const [actionStopIsDialogOpen, setActionStopIsDialogOpen] = useState(false);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleActionRead = () => {
    navigate(
      generatePath(APP_PATH.TRANSACTION.DETAILS, {
        id: resource.docs.at(0)?.id ?? 0,
      }),
    );
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const transaction = resource.docs.at(0);

  if (!transaction) {
    return <div />;
  }

  if (
    transaction.state === TransactionStateName.TRANSACTION_EXPIRED ||
    transaction.state === TransactionStateName.TRANSACTION_STOPPED
  ) {
    return (
      <ChargePointChannelTransactionContextProvider data={transaction}>
        <Stack>
          <Button
            onClick={() => {
              setActionStartIsDialogOpen(true);
            }}>
            {t('$.charge-point-channel-transaction-actions.start')}
          </Button>
        </Stack>
        <Dialog
          open={actionStartIsDialogOpen}
          onClose={() => setActionStartIsDialogOpen(false)}
          confirmClose
          confirmCloseMessageValidate={t(
            'shared-design-system.dialog.confirm-close-message-validate',
          )}
          confirmCloseMessage={t(
            'shared-design-system.dialog.confirm-close-message',
          )}
          confirmCloseMessageCancel={t(
            'shared-design-system.dialog.confirm-close-message-cancel',
          )}>
          <Stack direction="column" gap={GapEnum.M}>
            <Dialog.Header
              onClose={() => setActionStartIsDialogOpen(false)}
              title={t('$.charge-point-channel-transaction-start.title')}
              icon="wire"
            />
            <Dialog.Content>
              <ChargePointChannelTransactionStartController
                onCancel={() => setActionStartIsDialogOpen(false)}
                onSuccess={() => setActionStartIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Stack>
        </Dialog>
      </ChargePointChannelTransactionContextProvider>
    );
  }

  return (
    <>
      <Stack direction="column" gap={GapEnum.S}>
        <button type="button" onClick={handleActionRead}>
          <Stack
            gap={GapEnum.S}
            alignItems="center"
            justifyContent="space-between">
            <Stack alignItems="center" gap={GapEnum.XS}>
              <Icon name="credit-card-01" />
              {transaction.token?.visualNumber}
            </Stack>
            <Icon name="chevron-right" />
          </Stack>
        </button>
        <Stack>
          <Stack flex={1} className={css.chargePointChannelTransactionDetails}>
            <Stack flex={1}>
              <IconText
                iconName="lightning-02"
                text={`${transaction.kwh.toFixed(2)} kWh`}
              />
            </Stack>
            <Stack flex={1}>
              <IconText
                iconName="clock-fast-forward"
                text={getDuration(transaction.startedAt, new Date())}
              />
            </Stack>
          </Stack>
          <button
            className={css.stopTransactionButton}
            onClick={() => setActionStopIsDialogOpen(true)}>
            <Icon name="stop-circle" color={IconColor.Reverse} />
          </button>
        </Stack>
      </Stack>
      <Dialog
        open={actionStopIsDialogOpen}
        onClose={() => setActionStopIsDialogOpen(false)}
        confirmClose
        confirmCloseMessageValidate={t(
          'shared-design-system.dialog.confirm-close-message-validate',
        )}
        confirmCloseMessage={t(
          'shared-design-system.dialog.confirm-close-message',
        )}
        confirmCloseMessageCancel={t(
          'shared-design-system.dialog.confirm-close-message-cancel',
        )}>
        <Stack direction="column" gap={GapEnum.M}>
          <Dialog.Header
            onClose={() => setActionStopIsDialogOpen(false)}
            title={t('$.charge-point-channel-transaction-stop.title')}
            icon="wire"
          />
          <Dialog.Content>
            <ChargePointChannelTransactionStopController
              onCancel={() => setActionStopIsDialogOpen(false)}
              onSuccess={() => setActionStopIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Stack>
      </Dialog>
    </>
  );
};

export const ChargePointChannelTransactionDetailsPage =
  (): React.JSX.Element => {
    const userPermissions = useAppSelector(permissions);
    const channel = useChargePointChannelContext();
    const queryState = useCpmsBoGetTransactionsPaginatedQuery({
      channelId: channel.id,
      offset: 0,
      limit: 1,
      sortField: 'startedAt',
      sortOrder: -1,
    });

    const { Component } = withProtectedResource<
      TransactionsPaginatedModel,
      {},
      TransactionsPaginatedModel
    >(ChargePointChannelTransactionDetailsController, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.TRANSACTIONS,
          action: ActionsByResource.TRANSACTIONS.READ,
        }),
    })({});

    return Component;
  };
